import type { CmmdProjectMilestone, CmmdTaskStatus, CmmdWorkItemType } from "@cmmd-forge/contracts";
import { BotIcon, DiamondIcon, ListTreeIcon, SignalIcon, UserRoundIcon } from "lucide-react";

import {
  Combobox,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
} from "~/components/ui/combobox";
import { Avatar } from "~/components/ui/avatar";
import { PriorityIcon } from "~/components/projects/ProjectsPriorityIcon";
import {
  ProjectTaskBoardStatusIcon,
  ProjectTaskMilestoneIcon,
} from "./ProjectTaskMilestoneBoardIcons";
import { TaskWorkItemTypeIcon } from "./atoms/TaskWorkItemTypeIcon";

const EMPTY_VALUE = "__none__";

export interface ProjectTaskPickerOption {
  readonly value: string;
  readonly label: string;
  /**
   * Name only, for chips. The dropdown label may carry a disambiguator such as
   * an agent id — useful when choosing, noise once chosen.
   */
  readonly shortLabel?: string;
  readonly group?: string;
  readonly disabled?: boolean;
  readonly visual?: ProjectTaskPickerVisual;
}

export type ProjectTaskPickerVisual =
  | {
      readonly kind: "avatar";
      readonly name: string;
      readonly src: string | null;
    }
  | {
      readonly kind: "agent";
      readonly agentKind: string;
    }
  | {
      readonly kind: "priority";
      readonly priority: string;
    }
  | {
      readonly kind: "status";
      readonly colorClass: string;
    }
  | {
      readonly kind: "workflowStage";
      readonly color: string;
    }
  | {
      readonly kind: "boardStatus";
      readonly status: CmmdTaskStatus;
    }
  | {
      readonly kind: "milestone";
      readonly status: CmmdProjectMilestone["status"] | null;
    }
  | {
      readonly kind: "workItemType";
      readonly workItemType: CmmdWorkItemType;
    }
  | {
      readonly kind: "task";
    }
  | {
      readonly kind: "placeholder";
      readonly icon: "assignee" | "milestone" | "priority";
    };

export interface ProjectTaskPickerProps {
  readonly ariaLabel: string;
  readonly value: string | null;
  readonly emptyLabel: string;
  readonly allowEmpty?: boolean;
  readonly options: readonly ProjectTaskPickerOption[];
  readonly disabled: boolean;
  readonly presentation?: "field" | "chip" | "quietField";
  readonly emptyVisual?: ProjectTaskPickerVisual;
  readonly onValueChange: (value: string | null) => void;
}

export function ProjectTaskPicker(props: ProjectTaskPickerProps) {
  const values =
    props.allowEmpty === false
      ? props.options.map((option) => option.value)
      : [EMPTY_VALUE, ...props.options.map((option) => option.value)];
  const labelByValue = new Map([
    [EMPTY_VALUE, props.emptyLabel],
    ...props.options.map((option) => [option.value, option.label] as const),
  ]);
  const optionByValue = new Map(props.options.map((option) => [option.value, option] as const));
  const selectedOption = props.value ? optionByValue.get(props.value) : undefined;
  const selectedVisual = selectedOption?.visual ?? props.emptyVisual;
  const groups = Array.from(
    new Set(
      props.options.map((option) => option.group).filter((group): group is string => !!group),
    ),
  );

  const isQuietField = props.presentation === "quietField";
  return (
    <Combobox
      items={values}
      value={props.value ?? EMPTY_VALUE}
      itemToStringLabel={(item) => labelByValue.get(item) ?? item}
      itemToStringValue={(item) => labelByValue.get(item) ?? item}
      autoHighlight
      disabled={props.disabled}
      onValueChange={(nextValue) =>
        props.onValueChange(nextValue === EMPTY_VALUE ? null : nextValue)
      }
    >
      <ComboboxInput
        aria-label={props.ariaLabel}
        title={selectedOption?.label ?? props.emptyLabel}
        size="sm"
        startAddon={
          selectedVisual ? <ProjectTaskPickerVisualRenderer visual={selectedVisual} /> : undefined
        }
        {...(isQuietField
          ? {
              triggerClassName:
                "opacity-0 focus-visible:opacity-100 group-hover/quiet-detail:opacity-80",
            }
          : {})}
        inputClassName={
          props.presentation === "chip"
            ? "h-touch w-full truncate rounded-md border-border bg-muted px-3 shadow-none hover:bg-accent lg:h-8 lg:rounded-full"
            : isQuietField
              ? "h-touch border-transparent bg-transparent px-2 shadow-none hover:border-border hover:bg-muted focus-visible:border-ring lg:h-8"
              : "h-touch border-transparent bg-transparent px-2 shadow-none hover:border-border focus-visible:border-ring lg:h-8"
        }
      />
      <ComboboxPopup>
        <ComboboxEmpty>No matches</ComboboxEmpty>
        <ComboboxList>
          <ComboboxItem value={EMPTY_VALUE}>
            <ProjectTaskPickerItem label={props.emptyLabel} option={undefined} />
          </ComboboxItem>
          {props.options
            .filter((option) => !option.group)
            .map((option) => (
              <ComboboxItem key={option.value} value={option.value}>
                <ProjectTaskPickerItem label={option.label} option={option} />
              </ComboboxItem>
            ))}
          {groups.map((group) => (
            <ComboboxGroup key={group}>
              <ComboboxGroupLabel>{group}</ComboboxGroupLabel>
              {props.options
                .filter((option) => option.group === group)
                .map((option) => (
                  <ComboboxItem key={option.value} value={option.value} disabled={option.disabled}>
                    <ProjectTaskPickerItem label={option.label} option={option} />
                  </ComboboxItem>
                ))}
            </ComboboxGroup>
          ))}
        </ComboboxList>
      </ComboboxPopup>
    </Combobox>
  );
}

export function ProjectTaskPickerItem(props: {
  readonly label: string;
  readonly option: ProjectTaskPickerOption | undefined;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {props.option?.visual ? (
        <ProjectTaskPickerVisualRenderer visual={props.option.visual} />
      ) : null}
      <span className="truncate">{props.label}</span>
    </span>
  );
}

export function ProjectTaskPickerVisualRenderer(props: {
  readonly visual: ProjectTaskPickerVisual;
}) {
  if (props.visual.kind === "workflowStage") {
    return (
      <span
        className="size-2 rounded-full"
        style={{
          backgroundColor: props.visual.color.startsWith("#") ? props.visual.color : undefined,
        }}
        aria-hidden
      />
    );
  }
  if (isIdentityVisual(props.visual)) {
    return <IdentityVisual visual={props.visual} />;
  }
  if (isPlanningVisual(props.visual)) {
    return <PlanningVisual visual={props.visual} />;
  }
  if (isStructuralVisual(props.visual)) {
    return <StructuralVisual visual={props.visual} />;
  }
  return <span className={`size-2 rounded-full ${props.visual.colorClass}`} aria-hidden />;
}

type IdentityVisualValue = Extract<
  ProjectTaskPickerVisual,
  { readonly kind: "avatar" | "agent" | "priority" }
>;
type PlanningVisualValue = Extract<
  ProjectTaskPickerVisual,
  { readonly kind: "boardStatus" | "milestone" | "workItemType" }
>;
type StructuralVisualValue = Extract<
  ProjectTaskPickerVisual,
  { readonly kind: "task" | "placeholder" }
>;

function isIdentityVisual(visual: ProjectTaskPickerVisual): visual is IdentityVisualValue {
  return visual.kind === "avatar" || visual.kind === "agent" || visual.kind === "priority";
}

function isPlanningVisual(visual: ProjectTaskPickerVisual): visual is PlanningVisualValue {
  return ["boardStatus", "milestone", "workItemType"].includes(visual.kind);
}

function isStructuralVisual(visual: ProjectTaskPickerVisual): visual is StructuralVisualValue {
  return visual.kind === "task" || visual.kind === "placeholder";
}

function IdentityVisual(props: { readonly visual: IdentityVisualValue }) {
  if (props.visual.kind === "avatar") {
    return (
      <span aria-hidden>
        <Avatar name={props.visual.name} src={props.visual.src} className="size-5" />
      </span>
    );
  }
  if (props.visual.kind === "agent") {
    return (
      <span
        className="flex size-5 items-center justify-center rounded bg-primary/10 text-primary"
        aria-label={`${props.visual.agentKind} bot`}
      >
        <BotIcon className="size-3" aria-hidden />
      </span>
    );
  }
  return (
    <span aria-hidden>
      <PriorityIcon priority={props.visual.priority} />
    </span>
  );
}

function PlanningVisual(props: { readonly visual: PlanningVisualValue }) {
  if (props.visual.kind === "boardStatus") {
    return <ProjectTaskBoardStatusIcon status={props.visual.status} />;
  }
  if (props.visual.kind === "milestone") {
    return <ProjectTaskMilestoneIcon status={props.visual.status} />;
  }
  return (
    <span aria-hidden>
      <TaskWorkItemTypeIcon workItemType={props.visual.workItemType} />
    </span>
  );
}

function StructuralVisual(props: { readonly visual: StructuralVisualValue }) {
  if (props.visual.kind === "task") {
    return <ListTreeIcon className="size-3.5 text-muted-foreground" aria-hidden />;
  }
  const Icon =
    props.visual.icon === "assignee"
      ? UserRoundIcon
      : props.visual.icon === "milestone"
        ? DiamondIcon
        : SignalIcon;
  return <Icon className="size-3.5 text-muted-foreground" aria-hidden />;
}
